import { jwtDecode } from 'jwt-decode';
import { matchPath } from 'react-router-dom';
import { authStorage } from './auth-session';

/// Nome da claim emitida pelo IdentityService; se mudar de um lado só, o menu some para todos.
const CLAIM = 'Screens';

/// Rotas marcadas assim aparecem para qualquer autenticado (ex.: tela de lead, alcançada pelo card).
export const ALWAYS = 'always';

interface Props {
    /// Lista vale quando a tela junta várias: basta ter uma delas.
    permissions?: string | string[];
    collapse?: Props[];
    key?: string;
    route?: string;
    show?: boolean;
    /// Rota fora do menu: item que fica aceso enquanto ela está aberta.
    menuKey?: string;
}

/// O emissor manda string para um valor e array para vários; normalizar evita caso especial.
export const claimValues = (claim: string): Set<string> => {
    const session = authStorage.read();

    if (!session) return new Set();

    try {
        const raw = (jwtDecode(session.token) as Record<string, unknown>)[claim];

        if (typeof raw === 'string') return new Set([raw]);

        if (Array.isArray(raw)) return new Set(raw.map(String));

        return new Set();
    } catch {
        // Token ilegível é o mesmo que nenhuma permissão: na dúvida, esconde.
        return new Set();
    }
};

export const allowedScreens = (): Set<string> => claimValues(CLAIM);

export const canSee = (permission: string | string[] | undefined, screens: Set<string>): boolean => {
    if (Array.isArray(permission)) return permission.some((item) => canSee(item, screens));

    return permission === undefined || permission === ALWAYS || screens.has(permission);
};

/// Devolve a árvore só com o que a pessoa pode ver.
/// O grupo só sobrevive com filho visível no menu; os escondidos ficam, o roteador precisa deles.
export const filterRoutes = <T extends Props>(routes: T[], screens: Set<string>): T[] =>
    routes
        .map((route) => {
            if (!route.collapse) return canSee(route.permissions, screens) ? route : null;

            const children = filterRoutes(route.collapse, screens);

            return children.some((child) => child.show !== false)
                ? { ...route, collapse: children }
                : null;
        })
        .filter((route): route is T => route !== null);

/// Destino de quem chegou numa rota inexistente para ele: a primeira liberada, não uma fixa.
export const firstRoute = (routes: Props[]): string | null => {
    for (const route of routes) {
        if (route.collapse) {
            const found = firstRoute(route.collapse);

            if (found) return found;

            continue;
        }

        // Rota com parâmetro não serve de destino: /crm/lead/:leadId sem id não abre nada.
        if (route.show !== false && route.route && !route.route.includes(':')) return route.route;
    }

    return null;
};

/// A tela existe no sistema, liberada ou não: separa "não existe" (404) de "não é sua".
export const isKnownRoute = (routes: Props[], pathname: string): boolean =>
    routes.some((route) => route.collapse
        ? isKnownRoute(route.collapse, pathname)
        : Boolean(route.route && matchPath(route.route, pathname)));

/// Item do menu aceso na URL atual; rota fora do menu aponta o item pelo menuKey.
export const activeMenuKey = (routes: Props[], pathname: string): string | undefined => {
    for (const route of routes) {
        const found = route.collapse
            ? activeMenuKey(route.collapse, pathname)
            : route.route && matchPath(route.route, pathname) && (route.menuKey ?? route.key);

        if (found) return found;
    }

    return undefined;
};

/// Nível "All" do perfil; ausente vale como não, como no backend.
export const seesAllLeads = (): boolean => claimValues('LeadVisibility').has('All');
